<template>
  <div class="chat-widget" v-if="logged">
    <!-- 悬浮按钮 -->
    <div class="chat-fab" :class="{ 'is-open': visible }" @click="toggle()">
      <i :class="visible ? 'el-icon-close' : 'el-icon-chat-dot-round'"></i>
      <span class="chat-fab-badge" v-if="!visible && unread > 0">
        {{ unread > 99 ? "99+" : unread }}
      </span>
    </div>

    <!-- 聊天面板 -->
    <div class="chat-panel" v-show="visible">
      <div class="chat-header">
        <div class="chat-header-left">
          <span class="chat-dot" :class="{ online: agentOnline }"></span>
          <span class="chat-title">{{ agentName || $t("chat.title") }}</span>
          <span class="chat-conn" :class="{ on: connected }">
            {{ connected ? $t("chat.connected") : $t("chat.connecting") }}
          </span>
        </div>
        <i class="el-icon-close chat-header-close" @click="toggle(false)"></i>
      </div>

      <div class="chat-body" ref="body">
        <div v-if="loading" class="chat-center">
          <i class="el-icon-loading"></i>
        </div>
        <div v-else-if="messages.length === 0" class="chat-center chat-empty">
          {{ $t("chat.empty") }}
        </div>
        <template v-else>
          <div
            v-for="m in messages"
            :key="m.id || m.client_msg_id"
            class="chat-row"
            :class="{ mine: m.sender_type === 1 }"
          >
            <div v-if="m.status === 2" class="chat-sys">
              {{
                m.sender_type === 1
                  ? $t("chat.recalledSelf")
                  : $t("chat.recalledPeer")
              }}
            </div>
            <div v-else-if="m.msg_type === 5" class="chat-sys">
              {{ m.content }}
            </div>
            <div
              v-else
              class="chat-bubble"
              :class="{ mine: m.sender_type === 1, 'is-img': m.msg_type === 2 }"
            >
              <img
                v-if="m.msg_type === 2"
                :src="imgUrl(m.media_url)"
                class="chat-img"
                @click="preview(m.media_url)"
              />
              <span v-else v-html="formatText(m.content)"></span>
              <div class="chat-meta">
                <div>
                  <span>{{ m.create_time }}</span>
                </div>
                <div>
                  <span v-if="m.sender_type === 1" class="chat-read">
                    {{
                      m.id > 0 && m.id <= peerReadId
                        ? $t("chat.read")
                        : $t("chat.sent")
                    }}
                  </span>
                  <span
                    v-if="canRecall(m)"
                    class="chat-recall"
                    @click="onRecall(m)"
                    >{{ $t("chat.recall") }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="chat-typing" v-if="peerTyping">
        {{ $t("chat.typingPeer") }}
      </div>

      <div class="chat-footer">
        <textarea
          v-model="text"
          class="chat-input"
          :placeholder="$t('chat.placeholder')"
          maxlength="2000"
          @input="onInput"
          @keydown.enter.exact.prevent="send"
        ></textarea>
        <div class="chat-bar">
          <label class="chat-icon-btn" :title="$t('chat.image')">
            <i class="el-icon-picture-outline"></i>
            <input type="file" accept="image/*" @change="onPickImage" />
          </label>
          <span class="chat-tip">{{ tip }}</span>
          <button class="chat-send" :disabled="sending" @click="send">
            {{ sending ? $t("chat.sending") : $t("chat.send") }}
          </button>
        </div>
      </div>
    </div>

    <!-- 图片预览 -->
    <div class="chat-preview" v-if="previewUrl" @click="previewUrl = ''">
      <img :src="imgUrl(previewUrl)" />
    </div>
  </div>
</template>

<script>
import Const from "@/utils/consts";
import chatSocket from "@/utils/socket";

const genClientMsgId = () =>
  `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default {
  name: "ChatWidget",
  data() {
    return {
      logged: false,
      visible: false,
      connected: false,
      unread: 0,
      loading: false,
      sending: false,
      text: "",
      tip: "",
      session: null,
      messages: [],
      peerReadId: 0,
      peerTyping: false,
      typingTimer: null,
      typingSentAt: 0,
      /** 已上报过的最大消息 id（幂等去重） */
      lastReadSentId: 0,
      /** 上次上报已读的时间（节流用） */
      lastReadAt: 0,
      /** 节流合并定时器 */
      readTimer: null,
      previewUrl: "",
    };
  },
  computed: {
    agentOnline() {
      return !!this.session && this.session.admin_id > 0;
    },
    agentName() {
      return this.session && this.session.agent_name
        ? this.session.agent_name
        : "";
    },
  },
  mounted() {
    this.syncLogin();
    chatSocket.on("open", this.onOpen);
    chatSocket.on("close", this.onClose);
    chatSocket.on("chat.message", this.onMessage);
    chatSocket.on("chat.ack", this.onAck);
    chatSocket.on("chat.read", this.onRead);
    chatSocket.on("chat.recall", this.onRecallEvent);
    chatSocket.on("chat.typing", this.onTypingEvent);
    chatSocket.on("chat.session", this.onSessionEvent);
    chatSocket.on("chat.unread", this.onUnreadEvent);
    chatSocket.on("chat.error", this.onErrorEvent);
    chatSocket.on("chat.kick", this.onKick);
  },
  beforeDestroy() {
    if (this.readTimer) {
      clearTimeout(this.readTimer);
      this.readTimer = null;
    }
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
    chatSocket.off("open", this.onOpen);
    chatSocket.off("close", this.onClose);
    chatSocket.off("chat.message", this.onMessage);
    chatSocket.off("chat.ack", this.onAck);
    chatSocket.off("chat.read", this.onRead);
    chatSocket.off("chat.recall", this.onRecallEvent);
    chatSocket.off("chat.typing", this.onTypingEvent);
    chatSocket.off("chat.session", this.onSessionEvent);
    chatSocket.off("chat.unread", this.onUnreadEvent);
    chatSocket.off("chat.error", this.onErrorEvent);
    chatSocket.off("chat.kick", this.onKick);
  },
  watch: {
    // 登录/退出后自动同步（登录成功跳回页面后即可用）
    $route() {
      this.syncLogin();
    },
  },
  methods: {
    /** 同步登录态：已登录则建连并拉未读 */
    syncLogin() {
      const token = localStorage.getItem(Const.KEY_TOKEN);
      if (!token) {
        this.logged = false;
        this.unread = 0;
        this.messages = [];
        this.session = null;
        chatSocket.close();
        return;
      }
      this.logged = true;
      chatSocket.open();
      this.fetchUnread();
    },

    /** 未读数（WS 未连接时也能拿到角标） */
    async fetchUnread() {
      try {
        const res = await this.$api.chat.getUnread();
        if (res && res.code === 0 && res.data) {
          this.unread = res.data.total || 0;
        }
      } catch (e) {
        // 静默失败（未登录 / 网络异常）
      }
    },

    toggle(force) {
      const next = typeof force === "boolean" ? force : !this.visible;
      this.visible = next;
      if (next) {
        // 面板由 display:none 变为可见后，已有内容先滚到底（历史消息到达后会再滚一次）
        this.scrollToBottom(2);
        this.ensureSession();
      }
    },

    /** 获取/创建会话 + 拉历史 + 上报已读 */
    async ensureSession() {
      if (!localStorage.getItem(Const.KEY_TOKEN)) return;
      this.loading = true;
      try {
        const res = await this.$api.chat.getSession({ source: 1 });
        if (res && res.code === 0 && res.data) {
          this.session = res.data.session || null;
          // 聊天展示名：后台「客服设置 → 聊天展示昵称」有值就用它，
          // 否则（含后台清空昵称的情况）显式置空 -> 头部显示默认文案 $t("chat.title")
          if (this.session) {
            this.session.agent_name =
              (res.data.admin && res.data.admin.name) || "";
          }
          this.peerReadId = (this.session && this.session.admin_read_id) || 0;
          await this.loadMessages();
          this.markRead();
        }
      } catch (e) {
        this.tip = this.$t("chat.sessionFailed");
      } finally {
        this.loading = false;
        // 关键：必须等 loading=false 让消息列表真正渲染出来后再滚动。
        // loading 期间只渲染「加载中」占位，此时 scrollHeight 是错的（会停在顶部）；
        // 图片是异步渲染的，还会继续撑高内容，因此多补滚几次才能真正到底部。
        this.scrollToBottom(4);
      }
    },

    /** 历史消息（接口倒序返回，前端正序展示） */
    async loadMessages() {
      if (!this.session) return;
      try {
        const res = await this.$api.chat.getMessages({ page: 1, size: 50 });
        if (res && res.code === 0 && res.data) {
          this.messages = (res.data.data || []).slice().reverse();
          // 此处不滚动：此时 loading 仍为 true，消息还没进入 DOM（见 ensureSession 的 finally）
        }
      } catch (e) {
        // 静默失败
      }
    },

    /**
     * 上报已读（幂等 + 节流）
     *
     * 重要：服务端清未读后会回推 chat.unread，若这里不加防护就会形成
     * 「read -> chat.unread -> read」的无限循环（表现为 read 请求风暴）。
     * 因此做两层防护：① 已读到最新则不重复上报；② 1 秒内多次调用合并为一次。
     */
    markRead(force = false) {
      if (!this.session) return;
      const lastId = this.lastMsgId();

      // ① 没有可标记的消息，或已经读到最新 -> 跳过（真正打破循环的关键）
      if (!force && (lastId === 0 || lastId <= this.lastReadSentId)) return;

      // ② 节流：1 秒内的重复调用合并成一次
      const now = Date.now();
      if (!force && now - this.lastReadAt < 1000) {
        if (this.readTimer) return;
        this.readTimer = setTimeout(() => {
          this.readTimer = null;
          this.markRead();
        }, 1000);
        return;
      }

      this.readTimer = null;
      this.lastReadSentId = lastId;
      this.lastReadAt = now;
      this.unread = 0;

      // 已读优先走 WS（长连接在线时不再产生 HTTP read 请求）；断线时才走 HTTP 兜底
      if (chatSocket.isConnected()) {
        chatSocket.sendChat("chat.read", {
          session_id: this.session.id,
          read_id: lastId,
        });
      } else {
        this.$api.chat
          .read({ session_id: this.session.id, read_id: lastId })
          .catch(() => {});
      }
    },

    lastMsgId() {
      const arr = (this.messages || []).filter((m) => m.id > 0);
      return arr.length ? arr[arr.length - 1].id : 0;
    },

    /** 发送文本 */
    async send() {
      const content = (this.text || "").trim();
      if (!content || this.sending) return;
      this.text = "";
      await this.doSend({ msg_type: 1, content });
    },

    /** 选择图片 -> 上传 -> 发送图片消息 */
    async onPickImage(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        this.tip = this.$t("chat.imageTooLarge");
        return;
      }
      const form = new FormData();
      form.append("file", file);
      this.tip = this.$t("chat.uploading");
      try {
        const res = await this.$api.file.upload(form);
        const path = res && res.data ? res.data.path : "";
        this.tip = path ? "" : this.$t("chat.uploadFailed");
        if (path) {
          await this.doSend({ msg_type: 2, media_url: path });
        }
      } catch (err) {
        this.tip = this.$t("chat.uploadFailed");
      }
    },

    /** 发送消息：优先 WS，未连接走 HTTP 兜底 */
    async doSend(payload) {
      if (!this.session) {
        await this.ensureSession();
        if (!this.session) return;
      }
      this.sending = true;
      const clientMsgId = genClientMsgId();
      const temp = {
        id: 0,
        session_id: this.session.id,
        client_msg_id: clientMsgId,
        sender_type: 1,
        msg_type: payload.msg_type,
        content: payload.content || payload.media_url || "",
        media_url: payload.media_url || "",
        create_time: this.nowStr(),
        status: 1,
      };
      this.messages.push(temp);
      this.scrollToBottom();

      const body = {
        session_id: this.session.id,
        msg_type: payload.msg_type,
        content: payload.content,
        media_url: payload.media_url,
        client_msg_id: clientMsgId,
      };

      try {
        if (chatSocket.isConnected()) {
          chatSocket.sendChat("chat.send", body);
        } else {
          const res = await this.$api.chat.sendMessage(body);
          if (res && res.code === 0 && res.data) {
            this.replaceTemp(clientMsgId, res.data);
          } else {
            this.removeTemp(clientMsgId);
            this.tip = (res && res.msg) || this.$t("chat.sendFailed");
          }
        }
        // 本地更新会话摘要
        if (this.session) {
          this.session.last_msg_content =
            payload.msg_type === 2 ? "[图片]" : payload.content;
          this.session.last_msg_time = this.nowStr();
        }
      } catch (e) {
        this.removeTemp(clientMsgId);
        this.tip = this.$t("chat.sendFailed");
      } finally {
        this.sending = false;
      }
    },

    replaceTemp(clientMsgId, msg) {
      const idx = this.messages.findIndex(
        (m) => m.client_msg_id === clientMsgId,
      );
      if (idx >= 0) this.$set(this.messages, idx, msg);
      else this.messages.push(msg);
      this.scrollToBottom();
    },

    removeTemp(clientMsgId) {
      this.messages = this.messages.filter(
        (m) => m.client_msg_id !== clientMsgId,
      );
    },

    // ==================== WS 事件 ====================

    onOpen() {
      this.connected = true;
      this.tip = "";
      // 重连后补拉未读（服务端也会主动补推）
      this.fetchUnread();
    },

    onClose() {
      this.connected = false;
    },

    onMessage(msg) {
      if (!msg || !msg.id) return;
      const hit = this.messages.find(
        (m) =>
          m.id === msg.id ||
          (m.client_msg_id && m.client_msg_id === msg.client_msg_id),
      );
      if (hit) {
        // 同一消息（含发 Echo）直接覆盖
        const idx = this.messages.indexOf(hit);
        this.$set(this.messages, idx, msg);
      } else {
        this.messages.push(msg);
      }
      if (this.visible) {
        this.markRead();
      }
      // 图片消息高度要等加载完成，多补滚几次
      this.scrollToBottom(msg.msg_type === 2 ? 3 : 1);
    },

    onAck(data) {
      if (!data || !data.message) return;
      this.replaceTemp(data.client_msg_id, data.message);
    },

    onRead(data) {
      // read_type=2：客服已读 -> 我方消息显示"已读"
      if (data && data.read_type === 2) {
        this.peerReadId = Math.max(this.peerReadId, data.read_id || 0);
      }
    },

    /** 消息是否可撤回（本人、2 分钟内、未撤回） */
    canRecall(msg) {
      return (
        msg.sender_type === 1 &&
        msg.id > 0 &&
        msg.status !== 2 &&
        Date.now() - (msg.create_ts || 0) < 120 * 1000
      );
    },

    /** 撤回消息 */
    async onRecall(msg) {
      if (!this.session || !msg.id) return;
      this.$confirm(this.$t("chat.recallConfirm"), this.$t("chat.recall"), {
        confirmButtonText: this.$t("chat.confirm"),
        cancelButtonText: this.$t("chat.cancel"),
        type: "warning",
      })
        .then(async () => {
          try {
            const res = await this.$api.chat.recall({
              session_id: this.session.id,
              id: msg.id,
            });
            if (res && res.code === 0) {
              this.markRecalled(msg.id, msg.client_msg_id);
            } else {
              this.tip = (res && res.msg) || this.$t("chat.recallFailed");
            }
          } catch (e) {
            this.tip = this.$t("chat.recallFailed");
          }
        })
        .catch(() => {});
    },

    /** 本地把消息标记为已撤回 */
    markRecalled(id, clientMsgId) {
      const idx = this.messages.findIndex(
        (m) =>
          (id && m.id === id) ||
          (clientMsgId && m.client_msg_id === clientMsgId),
      );
      if (idx >= 0) {
        this.$set(this.messages, idx, { ...this.messages[idx], status: 2 });
      }
      if (this.session) this.session.last_msg_content = "[已撤回]";
    },

    /** 对端撤回事件（多端/双方同步） */
    onRecallEvent(data) {
      if (!data) return;
      this.markRecalled(data.id, data.client_msg_id);
    },

    /** 输入中（节流 3s 上报） */
    onInput() {
      const sess = this.session;
      if (!sess) return;
      const now = Date.now();
      if (now - this.typingSentAt < 3000) return;
      this.typingSentAt = now;
      chatSocket.sendChat("chat.typing", { session_id: sess.id });
    },

    /** 客服正在输入 */
    onTypingEvent(data) {
      if (!data || data.from_type !== 2) return; // 只关心客服
      if (this.session && data.session_id !== this.session.id) return;
      this.peerTyping = true;
      if (this.typingTimer) clearTimeout(this.typingTimer);
      this.typingTimer = setTimeout(() => {
        this.peerTyping = false;
      }, 3000);
    },

    onSessionEvent(data) {
      if (!this.session || !data || data.session_id !== this.session.id) return;
      this.session.status = data.status;
      this.session.admin_id = data.admin_id;
    },

    onUnreadEvent(data) {
      if (!data || typeof data.total !== "number") return;
      if (this.visible) {
        // 面板打开时角标直接清零
        this.unread = 0;
        // 仅在"确实还有未读"时才补一次已读；服务端回推 total=0 时不动作，
        // 否则会形成 read -> chat.unread -> read 的回声循环
        if (data.total > 0) this.markRead();
      } else {
        this.unread = data.total;
      }
    },

    onErrorEvent(data) {
      if (data && data.msg) this.tip = data.msg;
    },

    onKick(data) {
      this.tip = (data && data.msg) || this.$t("chat.kicked");
      localStorage.removeItem(Const.KEY_TOKEN);
      localStorage.removeItem(Const.KEY_INFO);
      setTimeout(() => {
        window.location.href = "/#/login";
      }, 800);
    },

    // ==================== 工具 ====================

    /** 图片地址（后端返回 /uploads/... 相对路径） */
    imgUrl(path) {
      if (!path) return "";
      if (/^https?:\/\//.test(path)) return path;
      return path;
    },

    preview(path) {
      this.previewUrl = path;
    },

    /** 文本转义 + 换行 */
    formatText(text) {
      const div = document.createElement("div");
      div.innerText = text || "";
      return div.innerHTML.replace(/\n/g, "<br/>");
    },

    nowStr() {
      const d = new Date();
      const p = (n) => (n < 10 ? "0" + n : "" + n);
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
        d.getHours(),
      )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },

    /**
     * 滚到底部
     *
     * retry = 补滚次数：图片（<img>）高度要等加载完成才确定，单次滚动会停在图片上方，
     * 因此隔一段时间再补几次；面板隐藏时 scrollHeight 为 0，滚动没有意义，直接跳过。
     */
    scrollToBottom(retry = 1) {
      if (!this.visible) return;
      const times = Math.max(1, retry);
      for (let i = 0; i < times; i++) {
        setTimeout(() => {
          this.$nextTick(() => {
            const el = this.$refs.body;
            if (el) el.scrollTop = el.scrollHeight;
          });
        }, i * 120);
      }
    },
  },
};
</script>

<style scoped>
.chat-widget {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2000;
  font-size: 14px;
}

/* 悬浮按钮 */
.chat-fab {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2b6cff, #00c2ff);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(43, 108, 255, 0.35);
  transition: transform 0.2s;
}
.chat-fab:hover {
  transform: scale(1.06);
}
.chat-fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ff4d4f;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}

/* 面板 */
.chat-panel {
  position: absolute;
  right: 0;
  bottom: 64px;
  width: 360px;
  height: 520px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #0a0e1a;
  color: #fff;
}
.chat-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.chat-title {
  font-weight: 600;
}
.chat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #bfbfbf;
}
.chat-dot.online {
  background: #52c41a;
}
.chat-conn {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}
.chat-conn.on {
  color: #52c41a;
}
.chat-header-close {
  cursor: pointer;
  font-size: 16px;
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f6f7fb;
}
.chat-center {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}
.chat-row {
  display: flex;
  margin-bottom: 10px;
}
.chat-row.mine {
  justify-content: flex-end;
}
.chat-bubble {
  max-width: 76%;
  padding: 8px 10px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #eaeaea;
  word-break: break-word;
  white-space: pre-wrap;
}
.chat-bubble.mine {
  background: #e8f1ff;
  border-color: #cfe2ff;
}
.chat-bubble.is-img {
  padding: 4px;
  background: #fff;
}
.chat-img {
  display: block;
  max-width: 200px;
  border-radius: 6px;
  cursor: zoom-in;
}
.chat-sys {
  margin: 0 auto;
  padding: 2px 8px;
  font-size: 12px;
  color: #999;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
}
.chat-meta {
  margin-top: 4px;
  font-size: 11px;
  color: #a0a0a0;
  display: flex;
  gap: 6px;
}
.chat-read {
  color: #2b6cff;
}
.chat-recall {
  color: #ff7875;
  cursor: pointer;
}
.chat-typing {
  padding: 4px 12px 0;
  font-size: 12px;
  color: #999;
}

.chat-footer {
  border-top: 1px solid #eee;
  padding: 8px 10px;
  background: #fff;
}
.chat-input {
  width: 100%;
  height: 56px;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 20px;
  font-family: inherit;
}
.chat-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.chat-icon-btn {
  cursor: pointer;
  font-size: 20px;
  color: #666;
}
.chat-icon-btn input[type="file"] {
  display: none;
}
.chat-tip {
  flex: 1;
  font-size: 12px;
  color: #ff7875;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.chat-send {
  min-width: 72px;
  height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 15px;
  background: linear-gradient(135deg, #2b6cff, #00c2ff);
  color: #fff;
  cursor: pointer;
}
.chat-send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chat-preview {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  cursor: zoom-out;
}
.chat-preview img {
  max-width: 80%;
  max-height: 80%;
}

@media (max-width: 480px) {
  .chat-panel {
    width: calc(100vw - 32px);
    height: 70vh;
  }
}
</style>
